import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
  ButtonBase,
  IconButton as MuiIconButton,
  Popover,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { useIsMobile } from '@side/pantry';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  activeViewAllGroup,
  anchoredPartnerCardId,
  isActiveViewAllGroupFamily,
  isAnyTeamDetailVisibleFamily,
  isGroupHoveredFamily,
  mailingAddressMatchesSelectedLocationFamily,
  selectedGroupKey,
  selectedPartnerMapTeamId,
  teamDetailPanelVisible,
  viewAllTeamId,
} from '@/common/atoms/community';
import { formatAddress } from '@/common/utils/address';
import { setImmediate } from '@/common/utils/setImmediate';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { usePinClass } from '../../hooks/usePinClass';
import type { TeamSearchResult } from '../../types';
import { GroupMapPin } from './GroupMapPin';

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  '& . Mui-disabled': {
    color: theme.palette.action.disabled,
  },
}));

interface Props {
  partners: TeamSearchResult[];
  groupKey: string;
  setMarker: (
    marker: google.maps.marker.AdvancedMarkerElement | null,
    key: string,
  ) => void;
}

export const GroupMarker = memo(
  ({ partners, groupKey, setMarker }: Props) => {
    const trackEvent = useTrackCommunityAnalytics();
    const isMobile = useIsMobile('md');
    const pinRef = useRef(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const isHovered = useAtomValue(isGroupHoveredFamily(groupKey));
    const partnerIds = useMemo(() => partners.map(({ id }) => id), [partners]);
    const isActiveViewAllMarker = useAtomValue(
      isActiveViewAllGroupFamily(groupKey),
    );
    const isGroupTeamSelected = useAtomValue(
      isAnyTeamDetailVisibleFamily(partnerIds),
    );

    const mailingAddressMatches = useAtomValue(
      mailingAddressMatchesSelectedLocationFamily(partners[0].mailingAddress),
    );

    const setSelectedTeamId = useSetAtom(selectedPartnerMapTeamId);
    const setTeamDetailPanelVisible = useSetAtom(teamDetailPanelVisible);
    const setSelectedGroup = useSetAtom(selectedGroupKey);
    const setAnchoredPartnerId = useSetAtom(anchoredPartnerCardId);
    const setActiveViewAllGroupKey = useSetAtom(activeViewAllGroup);
    const viewAllTeam = useAtomValue(viewAllTeamId);

    const position = {
      lat: partners[0].mailingAddress.geolocationLatitude,
      lng: partners[0].mailingAddress.geolocationLongitude,
    };

    const pinClass = usePinClass({
      active: popupOpen || isHovered || isGroupTeamSelected,
      selected: popupOpen || isGroupTeamSelected || isActiveViewAllMarker,
      address: partners[0].mailingAddress,
    });

    const setMarkerRef = useCallback(
      (ref: google.maps.marker.AdvancedMarkerElement | null) =>
        setMarker(ref, groupKey),
      [setMarker, groupKey],
    );

    const handleHover = useCallback(
      () =>
        trackEvent(TrackCommunityEvent.PartnerMapPinHover, {
          eventTarget: 'group_pin',
          inBoundary: !pinClass.includes('outOfBounds'),
          geoValue: [position.lat, position.lng],
          teamId: null,
          teamAddressId: null,
        }),
      [pinClass, position.lat, position.lng, trackEvent],
    );

    const selectGroup = useCallback(() => {
      const selectedTeamIndex = partners.findIndex(
        ({ id }) => id === viewAllTeam,
      );
      const index = selectedTeamIndex > -1 ? selectedTeamIndex : 0;

      setCurrentIndex(index);

      if (isMobile) {
        if (viewAllTeam) {
          setActiveViewAllGroupKey(groupKey);
          setSelectedTeamId(partners[0].id);
        } else {
          setPopupOpen(true);
        }
      } else {
        setSelectedGroup(groupKey);
        setSelectedTeamId(partners[index].id);
        setAnchoredPartnerId(partners[index].id);
        setTeamDetailPanelVisible(true);
      }
    }, [
      isMobile,
      groupKey,
      partners,
      viewAllTeam,
      setActiveViewAllGroupKey,
      setSelectedTeamId,
      setSelectedGroup,
      setAnchoredPartnerId,
      setTeamDetailPanelVisible,
    ]);

    const handleClick = useCallback(() => {
      selectGroup();
      trackEvent(TrackCommunityEvent.PartnerMapTeamSelection, {
        eventTarget: 'group_pin',
        inBoundary: !pinClass.includes('outOfBounds'),
        geoValue: [position.lat, position.lng],
        teamId: null,
        teamAddressId: null,
        teamOrder: null,
      });
    }, [pinClass, position.lat, position.lng, trackEvent, selectGroup]);

    const onPopupClick = useCallback(() => {
      setPopupOpen(false);
      setSelectedTeamId(partners[currentIndex].id);
      setAnchoredPartnerId(partners[currentIndex].id);
      setTeamDetailPanelVisible(true);
    }, [
      partners,
      currentIndex,
      setSelectedTeamId,
      setAnchoredPartnerId,
      setTeamDetailPanelVisible,
    ]);

    useEffect(() => {
      if (popupOpen && isMobile) {
        trackEvent(TrackCommunityEvent.PartnerMapMobileGroupTooltipLoad, {
          eventTarget: 'mobile_group_tooltip',
          geoValue: [position.lat, position.lng],
          teamId: null,
          teamAddressId: null,
          inBoundary: !pinClass.includes('outOfBounds'),
          groupOrder: currentIndex,
        });
      }
    }, [
      pinClass,
      position.lat,
      position.lng,
      popupOpen,
      isMobile,
      currentIndex,
      trackEvent,
    ]);

    useEffect(() => {
      if (mailingAddressMatches) {
        // Wait for pin to render before selecting to avoid popover shift
        setImmediate(() => requestAnimationFrame(selectGroup));
      }
    }, [mailingAddressMatches, selectGroup]);

    if (!partners.length) {
      return null;
    }

    const address = formatAddress({
      street1: partners[currentIndex].mailingAddress.street1,
      state: partners[currentIndex].mailingAddress.state,
      city: partners[currentIndex].mailingAddress.city,
      zip: partners[currentIndex].mailingAddress.postalCode,
    });
    const { legalName, alias } = partners[currentIndex];

    return (
      <AdvancedMarker
        position={position}
        onClick={handleClick}
        ref={setMarkerRef}
      >
        <Popover
          anchorEl={pinRef.current}
          open={popupOpen}
          onClose={() => setPopupOpen(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          slotProps={{
            paper: {
              sx: {
                width: 260,
              },
            },
          }}
          sx={{ mt: 0.5 }}
        >
          <Stack
            component={ButtonBase}
            disableRipple
            sx={{
              display: 'flex',
              width: '100%',
            }}
            onClick={onPopupClick}
          >
            <Stack
              sx={{
                gap: 0.5,
                p: 2,
                alignItems: 'flex-start',
                textAlign: 'left',
              }}
            >
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                {legalName?.length ? legalName : alias}
              </Typography>
              {legalName && alias && (
                <Typography
                  variant="caption"
                  sx={{ pt: 0.5, pb: 1, color: 'text.secondary' }}
                >
                  {alias}
                </Typography>
              )}
              <Typography variant="body3">{address}</Typography>
            </Stack>
            <Stack
              direction="row"
              sx={{
                pb: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                px: 1,
              }}
            >
              <IconButton
                aria-label="previous team"
                onClick={(e) => {
                  e.stopPropagation();

                  const prevIndex =
                    currentIndex === 0 ? partners.length - 1 : currentIndex - 1;
                  trackEvent(
                    TrackCommunityEvent.PartnerMapMobileGroupTooltipPaginatePreviousClick,
                    {
                      eventTarget: 'previous_button',
                      geoValue: [position.lat, position.lng],
                      groupOrder: prevIndex,
                    },
                  );
                  setCurrentIndex(prevIndex);
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {currentIndex + 1} of {partners.length}
              </Typography>
              <IconButton
                aria-label="next team"
                onClick={(e) => {
                  e.stopPropagation();

                  const nextIndex = (currentIndex + 1) % partners.length;
                  trackEvent(
                    TrackCommunityEvent.PartnerMapMobileGroupTooltipPaginateNextClick,
                    {
                      eventTarget: 'next_button',
                      geoValue: [position.lat, position.lng],
                      groupOrder: nextIndex,
                    },
                  );
                  setCurrentIndex(nextIndex);
                }}
              >
                <ArrowForward fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Popover>
        <GroupMapPin
          ref={pinRef}
          content={partners.length.toString()}
          className={pinClass}
          address={address}
          groupKey={groupKey}
          onHover={handleHover}
        />
      </AdvancedMarker>
    );
  },
  (prevProps, props) => isEqual(prevProps, props),
);

GroupMarker.displayName = 'GroupMarker';
